import React, { useState } from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { format, subDays, subWeeks, subMonths, startOfYear, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import DatePicker from '../datePicker';

interface DateRangePreset {
    label: string;
    getDateFrom: () => Date;
    getDateTo: () => Date;
}

interface ReflectionDateRangePickerProps {
    dateFrom: string;
    dateTo: string;
    onChange: (dateFrom: string, dateTo: string) => void;
}

const PRESETS: DateRangePreset[] = [
    { label: 'Сегодня', getDateFrom: () => subDays(new Date(), 1), getDateTo: () => new Date() },
    { label: 'Вчера', getDateFrom: () => subDays(new Date(), 2), getDateTo: () => subDays(new Date(), 1) },
    { label: 'За неделю', getDateFrom: () => subDays(new Date(), 7), getDateTo: () => new Date() },
    { label: 'За прошлую неделю', getDateFrom: () => subDays(new Date(), 14), getDateTo: () => subDays(new Date(), 7) },
    { label: 'За месяц', getDateFrom: () => subMonths(new Date(), 1), getDateTo: () => new Date() },
    { label: 'За прошлый месяц', getDateFrom: () => subMonths(new Date(), 2), getDateTo: () => subMonths(new Date(), 1) },
    { label: 'С начала года', getDateFrom: () => startOfYear(new Date()), getDateTo: () => new Date() },
];

export default function ReflectionDateRangePicker({ 
    dateFrom, 
    dateTo, 
    onChange 
}: ReflectionDateRangePickerProps) {
    const [customDateFrom, setCustomDateFrom] = useState<string>(dateFrom);
    const [customDateTo, setCustomDateTo] = useState<string>(dateTo);

    const handlePresetClick = (preset: DateRangePreset) => {
        const from = preset.getDateFrom();
        const to = preset.getDateTo();
        const fromStr = format(from, 'yyyy-MM-dd');
        const toStr = format(to, 'yyyy-MM-dd');
        onChange(fromStr, toStr);
    };

    return (
        <div>
            <h4 className="mb-3">Выберите период для рефлексии</h4>
            
            <Row className="mb-4">
                {PRESETS.map((preset) => (
                    <Col key={preset.label} xs={6} md={4} lg="auto" className="mb-2">
                        <Button 
                            variant="outline-primary" 
                            size="sm"
                            className="w-100"
                            onClick={() => handlePresetClick(preset)}
                        >
                            {preset.label}
                        </Button>
                    </Col>
                ))}
            </Row>

            <div className="p-3 mb-3 rounded" style={{ backgroundColor: 'var(--bs-secondary-bg)', color: 'var(--bs-secondary-color)', border: '1px solid var(--bs-border-color)' }}>
                <strong>Или выберите произвольный диапазон:</strong>
            </div>

            <Row className="mb-3">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Дата начала</Form.Label>
                        <div className="d-flex align-items-center gap-2">
                            <DatePicker 
                                date={customDateFrom ? parseISO(customDateFrom) : new Date()}
                                onSelect={(date) => {
                                    if (date) {
                                        const dateStr = format(date, 'yyyy-MM-dd');
                                        setCustomDateFrom(dateStr);
                                        if (customDateTo) {
                                            onChange(dateStr, customDateTo);
                                        }
                                    }
                                }}
                            />
                            {customDateFrom && (
                                <span className="text-muted small">
                                    {format(parseISO(customDateFrom), 'dd.MM.yyyy')}
                                </span>
                            )}
                        </div>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Дата окончания</Form.Label>
                        <div className="d-flex align-items-center gap-2">
                            <DatePicker 
                                date={customDateTo ? parseISO(customDateTo) : new Date()}
                                onSelect={(date) => {
                                    if (date) {
                                        const dateStr = format(date, 'yyyy-MM-dd');
                                        setCustomDateTo(dateStr);
                                        if (customDateFrom) {
                                            onChange(customDateFrom, dateStr);
                                        }
                                    }
                                }}
                            />
                            {customDateTo && (
                                <span className="text-muted small">
                                    {format(parseISO(customDateTo), 'dd.MM.yyyy')}
                                </span>
                            )}
                        </div>
                    </Form.Group>
                </Col>
            </Row>

            {dateFrom && dateTo && (
                <div className="p-3 mb-3 rounded" style={{ backgroundColor: 'rgba(var(--bs-success-rgb), 0.1)', color: 'var(--bs-success)', border: '1px solid var(--bs-success)' }}>
                    <strong>Выбран период:</strong>{' '}
                    {format(parseISO(dateFrom), 'd MMMM yyyy', { locale: ru })} — {' '}
                    {format(parseISO(dateTo), 'd MMMM yyyy', { locale: ru })}
                </div>
            )}
        </div>
    );
}
